"use client";

import styles from "@/styles/modules/components/contact/Module1.module.scss";
import FreeText from "../FreeText";
import TurnstileWidget from "../TurnstileWidget";
import { FormEvent, useCallback, useContext, useRef, useState } from "react";
import { LanguageContext } from "@/context/languageContext";
import { translate } from "@/utils/translate";
import { submitFormContact } from "@/wordpress/queries/formSubmit";

interface Module1Props {
  content: string | null;
}

export default function Module1({ content }: Module1Props) {
  const { language } = useContext(LanguageContext);

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState<boolean>(false);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken("");
  }, []);

  if (content === null) return;

  const resetForm = () => {
    if (nameRef.current) nameRef.current.value = "";
    if (phoneRef.current) phoneRef.current.value = "";
    if (emailRef.current) emailRef.current.value = "";
    if (messageRef.current) messageRef.current.value = "";
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!turnstileToken) {
      setResponseMessage(translate["formBookingError"][language]);
      setIsError(true);
      return;
    }

    setIsVerifying(true);

    try {
      const verifyRes = await fetch("/api/verify-turnstile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: turnstileToken }),
      });

      const verifyData = await verifyRes.json();

      if (!verifyData.success) {
        setResponseMessage(translate["formBookingError"][language]);
        setIsError(true);
        setIsVerifying(false);
        return;
      }
    } catch {
      setResponseMessage(translate["formBookingError"][language]);
      setIsError(true);
      setIsVerifying(false);
      return;
    }

    const response = await submitFormContact({
      name: nameRef.current!.value,
      email: emailRef.current!.value,
      phone: phoneRef.current!.value,
      message: messageRef.current!.value,
    });

    console.log("Response: ", response);
    console.log("Response.confirmation: ", response?.confirmation);

    if (response?.confirmation) {
      setResponseMessage(translate["formBookingSuccess"][language]);
      setIsError(false);
      resetForm();
      setTurnstileToken("");
    } else if (response?.errors?.length) {
      setResponseMessage(translate["formBookingError"][language]);
      setIsError(true);
    } else {
      setResponseMessage(translate["formBookingError"][language]);
      setIsError(true);
    }

    setIsVerifying(false);
  };

  return (
    <section className={styles.module1}>
      <div className={styles.module1Container}>
        <div className={styles.form}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formItem}>
              <h3>{translate["getInTouch"][language]}</h3>
            </div>
            <div className={styles.formItem}>
              <input
                type="text"
                name="name"
                id="name"
                placeholder={translate["nameMayus"][language]}
                ref={nameRef}
              />
            </div>
            <div className={styles.formItem}>
              <input
                type="text"
                name="email"
                id="email"
                placeholder={translate["emailMayus"][language]}
                ref={emailRef}
              />
            </div>
            <div className={styles.formItem}>
              <input
                type="text"
                name="phone"
                id="phone"
                placeholder={translate["telephone"][language]}
                ref={phoneRef}
              />
            </div>
            <div className={styles.formItem}>
              <textarea
                name="message"
                id="message"
                placeholder={translate["yourMessage"][language]}
                ref={messageRef}
              />
            </div>
            <div className={`${styles.formItem} ${styles.turnstileWrapper}`}>
              <TurnstileWidget
                siteKey={process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY!}
                onVerify={handleTurnstileVerify}
                onExpire={handleTurnstileExpire}
              />
            </div>
            <div className={styles.formItem}>
              <input
                type="submit"
                value={translate["send"][language]}
                disabled={isVerifying}
              />
            </div>
            {responseMessage && (
              <div
                className={styles.feedbackLabel}
                dangerouslySetInnerHTML={{ __html: responseMessage }}
              />
            )}
          </form>
        </div>
        <div className={styles.text}>
          <FreeText content={content} />
        </div>
      </div>
    </section>
  );
}
